import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    Card,
    CardContent,
    Divider,
    Button,
    TextField,
    Box,
    CardHeader,
} from '@mui/material';
import { useNotification } from 'config/Notification';

import {
    editCompany as editCompanyService,
    fetchSingleCompany as fetchSingleCompanyService,
    fetchApplications as fetchApplicationsService,
  } from 'services/modules/idp/company';
import Breadcrumbs from 'config/Breadcrumb';


const ConfigureServiceWeightages = () => {
  // Notification
  const { openNotification } = useNotification();

  // Redux dispatch
  const dispatch = useDispatch();
    
   // Redux selectors
   const selectedCompanyContainer = useSelector((state) => state.auth.companyData);
   const companyDataContainer = useSelector((state) => state.company.singleCompany);
   const servicesContainer = useSelector(state => state.company.applications);
   
   // State Variables
   const [companyId,setCompanyId]=useState(null);
   const [formData, setFormData] = useState({
        ppt_report: '',
        name: '',
        domain: '',
        contactname: '',
        email: '',
        companytype: '',
        country: '',
        location: {
        name: '',
        coords: [0, 0],
        },
        locate: '',
        employesize: '',
        currency: '',
        industry: '',
        subscriptionPlan: '',
        reportFrequency: '',
        cloudtypes: [],
        applications: [],
        applicationWeightages: [],
        webapplications: [],
        network_locations: [],
        mobiletypes: [],
        category: [],
        mobile_code: '',
        mobile_digits: '',
        heatmap: '',
        supplier_type: '',
        partner: '',
        mobile: {
        code: '',
        digits: ''
        }
    });
    
    

    useEffect(() => {
        if(selectedCompanyContainer){
            setCompanyId(selectedCompanyContainer.companyid);
        }
    }, [selectedCompanyContainer]);

    useEffect(() => {
        if(companyId){
            dispatch(fetchSingleCompanyService({ companyId }));
            dispatch(fetchApplicationsService());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [companyId]);

    useEffect(() => {
        if (companyDataContainer) {
          setFormData({
            ppt_report: companyDataContainer?.ppt_report,
            name: companyDataContainer?.name,
            domain: companyDataContainer?.domain,
            contactname: companyDataContainer?.contactname,
            email: companyDataContainer?.email || '',
            companytype: companyDataContainer.companytype || '',
            country: companyDataContainer.country || '',
            partner: companyDataContainer.partner || '',
            location: {
              name: '',
              coords: [0, 0]
            },
            locate: companyDataContainer.location ? companyDataContainer.location.name : '',
            employesize: companyDataContainer.employesize || '',
            category: companyDataContainer.applications?.filter((app)=>app.slug === 'vapt').length>0 
            ?  companyDataContainer?.category : [],
            currency: companyDataContainer.currency || '',
            industry: companyDataContainer.industry ? companyDataContainer.industry.id : '',
            subscriptionPlan: companyDataContainer.subscriptionPlan ? companyDataContainer.subscriptionPlan.id : '',
            reportFrequency: companyDataContainer.reportFrequency ? companyDataContainer.reportFrequency.id : '',
            applications: companyDataContainer.applications
              ? (Array.isArray(companyDataContainer.applications)
                ? companyDataContainer.applications.map((application) => application.id)
                : [companyDataContainer.applications.id])
              : [],
            applicationWeightages: companyDataContainer?.applicationWeightages
              ? companyDataContainer.applicationWeightages.map(({ application, weightage }) => ({
                  application: application.id,
                  weightage
                }))
              : [],
            cloudtypes: companyDataContainer?.applications?.filter((app)=>app.slug === 'vapt').length>0 
            ?  companyDataContainer?.cloudtypes : [],
            webapplications : companyDataContainer?.webapplications || [],
            network_locations : companyDataContainer?.network_locations || [],
            mobiletypes : companyDataContainer?.mobiletypes || [],
            mobile_code: companyDataContainer?.mobile?.code,
            mobile_digits: companyDataContainer?.mobile?.digits,
            heatmap: companyDataContainer?.heatmap || '',
            supplier_type: companyDataContainer?.supplier_type || '',
            mobile: {
              code: '',
              digits: ''
            }
          });
        }
      }, [companyDataContainer]);
    
    const handleWeightageInputChange = (event, appId) => {
    const { value } = event.target;

    if (!isNaN(value) && value >= 0 && value <= 100) {
        setFormData((prevFormData) => {
        const updatedApplicationWeightages = prevFormData.applicationWeightages.map(weightage => 
            weightage.application === appId 
            ? { ...weightage, weightage: Number(value) }
            : weightage
        );

        // If the application was not found, add a new entry
        if (!updatedApplicationWeightages.find(weightage => weightage.application === appId)) {
            updatedApplicationWeightages.push({ application: appId, weightage: Number(value) });
        }

        return {
            ...prevFormData,
            applicationWeightages: updatedApplicationWeightages,
        };
        });
    }
    };

  const getApplicationName=(app)=>
  {
      if(app==='CAP')
        return "InfoSec Compliance"
      else if(app === "ECS")
        return "External Risk"
      else
      return `${app} (Internal Risk)`
  }

  // Handles the form submission 
  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      
      // Check that the sum of weightages equals 100
      const totalWeightage = formData.applicationWeightages.reduce((sum, weightage) => sum + weightage.weightage, 0);
      if (totalWeightage !== 100) {
        openNotification('The sum of all weightages must equal 100', 'error');
        return;
      }
      const updatedFormData = { ...formData };

      delete updatedFormData.partner;
      updatedFormData.mobile = {
        code: formData.mobile_code,
        digits: formData.mobile_digits,
      };
      delete updatedFormData.mobile_code;
      delete updatedFormData.mobile_digits;
      delete updatedFormData.domain;

      updatedFormData.location = {
        name: updatedFormData.locate,
        coords: [0, 0],
      };
      delete updatedFormData.locate;

      if (updatedFormData.supplier_type === '')
        delete updatedFormData.supplier_type
      if (updatedFormData.heatmap === '')
        delete updatedFormData.heatmap;
      
      const response = await editCompanyService(companyId, updatedFormData)
      if (response?.status === 200 || response?.status === 201) {
        openNotification('Company edited successfully', 'success')
        dispatch(fetchSingleCompanyService({ companyId }));
      } else {
        openNotification(response, 'error')
      }

    } catch (error) {
      openNotification('Error while editing company', 'error')
    }
  };

    const centerCardStyle = {
        textAlign: 'center',
    };

    const dividerStyle = {
        margin: '20px 0',
    };
    
    return (
        <div className='main-cont'>
            <Card>
            <CardHeader
          title={
            <React.Fragment>
              <div className="card-head">
                <div className="flex-head">
                  <label className="label">
                  <Breadcrumbs paths={['Configurator','ISP Weightages']} />
                  </label>
                </div>
                <div>
                  <Button
                    variant="contained"
                    className="back font"
                    onClick={() => window.history.back()}
                  >
                    Back
                  </Button>
                </div>
              </div>
            </React.Fragment>
          }
        />
            <CardContent>
                <Divider style={dividerStyle} />
                <CardContent style={centerCardStyle}>
                    <div className="main-cont">
                        <div className="container">
                            <div className="row">
                                {servicesContainer && servicesContainer.map((application) => {
                                    // Find the weightage for the current application
                                    const weightageObj = formData.applicationWeightages.find(weightage => weightage.application === application.id);
                                    const weightageValue = weightageObj ? weightageObj.weightage : 0;

                                    // Check if this application is in formData.applications (editable)
                                    const isEditable = formData.applications.includes(application.id);

                                    return (
                                    <div className="col-md-6 mb-2" key={application.id}> {/* Two columns per row */}
                                        <div className="d-flex align-items-center">
                                        <strong style={{ marginRight: '10px', width: '150px' }}>{getApplicationName(application.label)}</strong>
                                        <TextField
                                            label="Weightage"
                                            value={weightageValue}
                                            onChange={(e) => handleWeightageInputChange(e, application.id)}
                                            margin="normal"
                                            inputProps={{ step: '0.1' }}
                                            disabled={!isEditable}
                                            />
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                            <div className='row'>
                                <Box display="flex" justifyContent="center" mt={3}>
                                    <Button color="primary" variant="contained" onClick={(e) => handleSubmit(e)} >
                                        Submit
                                    </Button>
                                </Box>
                            </div>
                        </div>
                    </div>
                
                </CardContent>
                </CardContent>
            </Card>
        </div>
    );
};
export default ConfigureServiceWeightages;
